"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { getSubscriptionAgreement } from "../../api/getSubscriptionAgreement";
import type { SubscriptionAgreementData } from "../../api/getSubscriptionAgreement";
import { signSubscriptionAgreement } from "../../api/signSubscriptionAgreement";
import { SignaturePad } from "./SignaturePad";

interface SubscriptionAgreementStepProps {
  caraSku: string;
  defaultName: string;
  defaultEmail: string;
  /** Called with the signed-agreement uuid once signing succeeds. */
  onSigned: (signedAgreementUuid: string) => void;
  /** Back out of checkout (returns to the landing page). */
  onBack: () => void;
}

/**
 * Step 1 of subscription checkout: review + sign the agreement. Payment is
 * not shown until this completes — no signature, no charge, and the
 * contract is presented BEFORE any payment is taken.
 */
export function SubscriptionAgreementStep({
  caraSku,
  defaultName,
  defaultEmail,
  onSigned,
  onBack,
}: SubscriptionAgreementStepProps) {
  const [data, setData] = useState<SubscriptionAgreementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signerName, setSignerName] = useState(defaultName);
  const [signerEmail, setSignerEmail] = useState(defaultEmail);
  const [isSigning, setIsSigning] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSubscriptionAgreement(caraSku)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caraSku]);

  useEffect(() => {
    setSignerName(defaultName);
    setSignerEmail(defaultEmail);
  }, [defaultName, defaultEmail]);

  // If the agreement fits without scrolling, there's nothing to scroll — treat
  // it as read so the button can't get stuck disabled.
  useEffect(() => {
    if (!data) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 4) {
      setScrolledToEnd(true);
    }
  }, [data]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      setScrolledToEnd(true);
    }
  };

  const canSubmit = Boolean(
    data &&
      scrolledToEnd &&
      signatureDataUrl &&
      signerName.trim() &&
      signerEmail.trim() &&
      !isSigning,
  );

  // Spell out exactly what's still required so a disabled button never
  // leaves the user guessing why they can't continue.
  const missingSteps: string[] = [];
  if (!scrolledToEnd) missingSteps.push("scroll to the end of the agreement");
  if (!signerName.trim() || !signerEmail.trim())
    missingSteps.push("enter your name and email");
  if (!signatureDataUrl) missingSteps.push("add your signature");

  const handleSign = () => {
    if (!canSubmit || !signatureDataUrl) return;
    setIsSigning(true);
    signSubscriptionAgreement({
      caraSku,
      signerName: signerName.trim(),
      signerEmail: signerEmail.trim(),
      signatureDataUrl,
    })
      .then((res) => onSigned(res.data.uuid))
      .finally(() => setIsSigning(false));
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white">
        Review &amp; Sign Your Agreement
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Please read the {data?.offeringName ?? "subscription"} agreement in full
        and sign below. You&rsquo;ll enter payment on the next step — your card
        is not charged until you&rsquo;ve signed.
      </p>

      {loading ? (
        <div className="py-10">
          <LoadingIndicator />
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {/*
            The legacy agreement HTML carries hardcoded inline colors
            (dark navy body / dark red headings) that are unreadable on a
            dark background. Force a legible color on all descendants in
            dark mode (the `!` overrides the inline style); light mode keeps
            the original styling.
          */}
          <div
            className="max-h-[42vh] overflow-y-auto rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-4 text-sm text-gray-700 dark:text-gray-200 leading-relaxed dark:[&_*]:!text-gray-100"
            dangerouslySetInnerHTML={{ __html: data?.agreementHtml ?? "" }}
            onScroll={handleScroll}
            ref={scrollRef}
          />
          {scrolledToEnd ? (
            <p className="text-xs font-medium text-green-600 dark:text-green-400">
              ✓ You&rsquo;ve reviewed the full agreement.
            </p>
          ) : (
            <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
              ↓ Scroll to the bottom of the agreement to continue.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="agreement-signer-name">Full Name</Label>
              <Input
                id="agreement-signer-name"
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Your full name"
                value={signerName}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agreement-signer-email">Email</Label>
              <Input
                id="agreement-signer-email"
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="you@company.com"
                type="email"
                value={signerEmail}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Signature</Label>
            <SignaturePad onChange={setSignatureDataUrl} />
          </div>

          <div className="flex flex-col gap-3 pt-2">
            {!isSigning && missingSteps.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Before continuing, please {missingSteps.join(", ")}.
              </p>
            )}
            <div className="flex justify-between gap-4">
              <Button onClick={onBack} type="button" variant="outline">
                Back
              </Button>
              <Button disabled={!canSubmit} onClick={handleSign} type="button">
                {isSigning ? "Signing..." : "Agree & Continue to Payment"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionAgreementStep;
